import React, { useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  pdfMerge, pdfSplit, pdfCompress, imagesToPdf,
  imageConvert, imageCompress, imageResize,
  qrGenerate, bumpUsage,
} from "@/lib/clientTools";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Copy, Download, Loader2, ShieldCheck, Sparkles, Upload, RefreshCw, Check,
} from "lucide-react";
import { CATEGORIES, TOOLS, getTool } from "../lib/tools";
import AdSlot from "../components/AdSlot";
import Seo, { toolJsonLd } from "../components/Seo";
import ToolCard from "../components/ToolCard";

// AI runs on a same-origin Cloudflare Pages Function. Everything else is client-side.
const API = "/api";

function CopyBtn({ value, testid = "copy-btn" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => {
        if (!value) return;
        navigator.clipboard.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      className="btn-secondary"
    >
      {done ? <Check size={16} /> : <Copy size={16} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

function DownloadBlobBtn({ blob, filename, testid = "download-btn" }) {
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  return (
    <a data-testid={testid} href={url} download={filename} className="btn-primary">
      <Download size={16} /> Download {filename}
    </a>
  );
}

function FilePicker({ accept, multiple, onChange, testid = "file-input", label = "Click to upload or drop files" }) {
  const ref = useRef();
  const [files, setFiles] = useState([]);
  function set(list) {
    const arr = Array.from(list || []);
    setFiles(arr);
    onChange(arr);
  }
  return (
    <div>
      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); set(e.dataTransfer.files); }}
        className="block border-2 border-dashed border-border hover:border-primary transition-colors rounded-xl p-10 text-center cursor-pointer bg-secondary"
      >
        <Upload className="mx-auto mb-3 text-gray-400" size={26} />
        <p className="font-medium text-foreground">{label}</p>
        <p className="text-xs text-gray-500 mt-1">{accept}</p>
        <input ref={ref} type="file" accept={accept} multiple={multiple}
          className="hidden" data-testid={testid}
          onChange={(e) => set(e.target.files)} />
      </label>
      {files.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="file-list">
          {files.map((f, i) => (
            <li key={i} className="text-sm text-gray-600 font-mono truncate">• {f.name} <span className="text-gray-400">({(f.size/1024).toFixed(1)} KB)</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* =========== AI RUNNER =========== */
function AIRunner({ tool }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [target, setTarget] = useState("Spanish");
  const [tone, setTone] = useState("professional");

  async function run() {
    setErr(""); setResult(""); setLoading(true);
    try {
      const options = {};
      if (tool.action === "translate") options.target_language = target;
      if (tool.action === "email") options.tone = tone;
      const r = await axios.post(`${API}/ai/process`, {
        action: tool.action, text, options,
      });
      setResult(r.data.result);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  }

  const inputLabel = {
    summarize: "Paste the article or notes to summarize",
    paraphrase: "Paste the text you want rewritten",
    grammar: "Paste your text to be corrected",
    translate: "Paste text to translate",
    email: "Describe the email you want — context, recipient, ask",
    tagline: "Describe the brand/product (one or two sentences)",
    explain_code: "Paste the code snippet",
    ideas: "What do you need ideas for?",
  }[tool.action];

  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-medium text-gray-700">{inputLabel}</label>
        <textarea
          data-testid="ai-input"
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Start typing or paste content…"
          className="input mt-2" rows={9}
        />
        <div className="text-xs text-gray-400 mt-1 font-mono">{text.length} / 20,000</div>
      </div>

      {tool.action === "translate" && (
        <div>
          <label className="text-sm font-medium text-gray-700">Target language</label>
          <select className="input mt-2" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="ai-target-lang">
            {["Spanish","French","German","Italian","Portuguese","Dutch","Polish","Russian","Arabic","Hindi","Bengali","Chinese (Simplified)","Japanese","Korean","Turkish","Vietnamese","Indonesian","Thai","Swedish","Norwegian","Greek","Hebrew","Ukrainian","Czech","Romanian","Hungarian","Finnish","Danish","Tagalog","English"].map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      )}

      {tool.action === "email" && (
        <div>
          <label className="text-sm font-medium text-gray-700">Tone</label>
          <select className="input mt-2" value={tone} onChange={(e) => setTone(e.target.value)} data-testid="ai-tone">
            {["professional","friendly","apologetic","persuasive","concise","formal"].map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="ai-run-btn"
          disabled={loading || !text.trim()}
          onClick={run}
          className="btn-primary"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? "Working…" : "Run"}
        </button>
        <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-emerald-500" /> 100% Free · No Signup · Secure
        </span>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-3" data-testid="ai-error">{err}</div>}

      {result && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="ai-result">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Result</span>
            <CopyBtn value={result} testid="ai-copy-btn" />
          </div>
          <pre className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground font-sans">{result}</pre>
        </div>
      )}
    </div>
  );
}

/* =========== FILE UPLOAD RUNNERS =========== */
function FileUploadRunner({ tool }) {
  const [files, setFiles] = useState([]);
  const [extra, setExtra] = useState({ ranges: "", target_format: "png", quality: 70, width: 800, height: 0, keep_aspect: true });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [outBlob, setOutBlob] = useState(null);
  const [outName, setOutName] = useState("");

  const cfg = {
    pdf_merge:       { multi: true,  accept: "application/pdf", label: "Drop 2 or more PDFs" },
    pdf_split:       { multi: false, accept: "application/pdf", label: "Drop a PDF" },
    pdf_compress:    { multi: false, accept: "application/pdf", label: "Drop a PDF" },
    images_to_pdf:   { multi: true,  accept: "image/*",         label: "Drop one or more images" },
    image_convert:   { multi: false, accept: "image/*",         label: "Drop an image" },
    image_compress:  { multi: false, accept: "image/*",         label: "Drop an image" },
    image_resize:    { multi: false, accept: "image/*",         label: "Drop an image" },
  }[tool.kind];

  async function run() {
    setErr(""); setOutBlob(null); setLoading(true);
    try {
      // All processing happens in the browser. Files never leave the device.
      let out;
      switch (tool.kind) {
        case "pdf_merge":      out = await pdfMerge(files); break;
        case "pdf_split":      out = await pdfSplit(files[0], extra.ranges); break;
        case "pdf_compress":   out = await pdfCompress(files[0]); break;
        case "images_to_pdf":  out = await imagesToPdf(files); break;
        case "image_convert":  out = await imageConvert(files[0], extra.target_format); break;
        case "image_compress": out = await imageCompress(files[0], extra.quality); break;
        case "image_resize":   out = await imageResize(files[0], extra.width, extra.height, extra.keep_aspect); break;
        default: throw new Error("Unknown tool");
      }
      setOutBlob(out.blob);
      setOutName(out.name);
      bumpUsage("files_processed", files.length);
      bumpUsage("tools_run");
    } catch (e) {
      setErr(e?.message || "Something went wrong");
    } finally { setLoading(false); }
  }

  const canRun = files.length > 0 && (!cfg.multi || files.length >= (tool.kind === "pdf_merge" ? 2 : 1));

  return (
    <div className="space-y-5">
      <FilePicker accept={cfg.accept} multiple={cfg.multi} label={cfg.label}
        onChange={setFiles} testid="file-input" />

      {tool.kind === "pdf_split" && (
        <div>
          <label className="text-sm font-medium text-gray-700">Pages or ranges (leave blank for all)</label>
          <input className="input mt-2 font-mono" placeholder="e.g. 1-3,5,7-9" value={extra.ranges}
            onChange={(e) => setExtra({ ...extra, ranges: e.target.value })} data-testid="pdf-ranges" />
        </div>
      )}

      {tool.kind === "image_convert" && (
        <div>
          <label className="text-sm font-medium text-gray-700">Target format</label>
          <select className="input mt-2" value={extra.target_format} onChange={(e) => setExtra({ ...extra, target_format: e.target.value })} data-testid="img-format">
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
            <option value="webp">WebP</option>
          </select>
        </div>
      )}

      {tool.kind === "image_compress" && (
        <div>
          <label className="text-sm font-medium text-gray-700">Quality: {extra.quality}</label>
          <input type="range" min="10" max="95" value={extra.quality}
            onChange={(e) => setExtra({ ...extra, quality: Number(e.target.value) })}
            className="w-full mt-2" data-testid="img-quality" />
        </div>
      )}

      {tool.kind === "image_resize" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Width (px)</label>
            <input type="number" className="input mt-2" value={extra.width}
              onChange={(e) => setExtra({ ...extra, width: Number(e.target.value) })} data-testid="img-width" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Height (px)</label>
            <input type="number" className="input mt-2" value={extra.height}
              onChange={(e) => setExtra({ ...extra, height: Number(e.target.value) })} data-testid="img-height" />
          </div>
          <div className="col-span-2 flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={extra.keep_aspect}
              onChange={(e) => setExtra({ ...extra, keep_aspect: e.target.checked })} data-testid="img-keep-aspect" />
            Keep aspect ratio
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="file-run-btn" disabled={!canRun || loading} onClick={run} className="btn-primary">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? "Processing…" : "Run"}
        </button>
        <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-emerald-500" /> 100% Free · No Signup · Files processed securely
        </span>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-3" data-testid="file-error">{err}</div>}

      {outBlob && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="file-result">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600">Ready</div>
              <div className="font-heading text-lg font-medium mt-1">Your file is ready to download</div>
            </div>
            <DownloadBlobBtn blob={outBlob} filename={outName} testid="file-download-btn" />
          </div>
        </div>
      )}
    </div>
  );
}

/* =========== CLIENT-SIDE RUNNERS =========== */
function QRRunner() {
  const [text, setText] = useState(typeof window !== "undefined" ? window.location.origin : "");
  const [size, setSize] = useState(10);
  const [img, setImg] = useState("");
  const [loading, setLoading] = useState(false);
  async function run() {
    setLoading(true);
    try {
      const dataUrl = await qrGenerate(text, size); // generated in-browser, instant
      setImg(dataUrl);
      bumpUsage("tools_run");
    } finally { setLoading(false); }
  }
  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-medium text-gray-700">URL or text</label>
        <input className="input mt-2" value={text} onChange={(e) => setText(e.target.value)} data-testid="qr-text" />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700">Size: {size}</label>
        <input type="range" min="4" max="20" value={size} onChange={(e) => setSize(Number(e.target.value))} className="w-full mt-2" data-testid="qr-size" />
      </div>
      <button onClick={run} disabled={loading || !text} className="btn-primary" data-testid="qr-run-btn">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate
      </button>
      {img && (
        <div className="border border-border rounded-xl bg-card p-6 flex flex-col items-start gap-4" data-testid="qr-result">
          <img src={img} alt="QR" className="border border-border rounded" />
          <a href={img} download="qrcode.png" className="btn-secondary" data-testid="qr-download">
            <Download size={16} /> Download PNG
          </a>
        </div>
      )}
    </div>
  );
}

function JsonRunner() {
  const [input, setInput] = useState('{"hello":"world","items":[1,2,3]}');
  const [output, setOutput] = useState("");
  const [err, setErr] = useState("");
  function format(min = false) {
    try {
      const parsed = JSON.parse(input);
      setOutput(JSON.stringify(parsed, null, min ? 0 : 2));
      setErr("");
    } catch (e) { setErr("Invalid JSON: " + e.message); setOutput(""); }
  }
  return (
    <div className="space-y-5">
      <textarea className="input font-mono" rows={10} value={input} onChange={(e) => setInput(e.target.value)} data-testid="json-input" />
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => format(false)} className="btn-primary" data-testid="json-format-btn">Beautify</button>
        <button onClick={() => format(true)} className="btn-secondary" data-testid="json-minify-btn">Minify</button>
      </div>
      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-3" data-testid="json-error">{err}</div>}
      {output && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="json-output">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Output</span>
            <CopyBtn value={output} testid="json-copy" />
          </div>
          <pre className="font-mono text-sm whitespace-pre-wrap text-foreground overflow-x-auto">{output}</pre>
        </div>
      )}
    </div>
  );
}

function Base64Runner() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState("encode");
  function run() {
    try {
      setOutput(mode === "encode" ? btoa(unescape(encodeURIComponent(input))) : decodeURIComponent(escape(atob(input.trim()))));
    } catch { setOutput("Invalid input for " + mode); }
  }
  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        <button className={mode === "encode" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("encode")} data-testid="b64-encode-btn">Encode</button>
        <button className={mode === "decode" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("decode")} data-testid="b64-decode-btn">Decode</button>
      </div>
      <textarea className="input font-mono" rows={6} value={input} onChange={(e) => setInput(e.target.value)} placeholder={mode === "encode" ? "Plain text" : "Base64 string"} data-testid="b64-input" />
      <button onClick={run} className="btn-primary" data-testid="b64-run-btn"><Sparkles size={16} /> Run</button>
      {output && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="b64-output">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Output</span>
            <CopyBtn value={output} testid="b64-copy" />
          </div>
          <pre className="font-mono text-sm whitespace-pre-wrap break-all">{output}</pre>
        </div>
      )}
    </div>
  );
}

function UrlRunner() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState("encode");
  function run() {
    try {
      setOutput(mode === "encode" ? encodeURIComponent(input) : decodeURIComponent(input));
    } catch { setOutput("Invalid input"); }
  }
  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        <button className={mode === "encode" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("encode")} data-testid="url-encode-btn">Encode</button>
        <button className={mode === "decode" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("decode")} data-testid="url-decode-btn">Decode</button>
      </div>
      <textarea className="input font-mono" rows={5} value={input} onChange={(e) => setInput(e.target.value)} data-testid="url-input" />
      <button onClick={run} className="btn-primary" data-testid="url-run-btn"><Sparkles size={16} /> Run</button>
      {output && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="url-output">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Output</span>
            <CopyBtn value={output} testid="url-copy" />
          </div>
          <pre className="font-mono text-sm whitespace-pre-wrap break-all">{output}</pre>
        </div>
      )}
    </div>
  );
}

function UuidRunner() {
  const [count, setCount] = useState(10);
  const [list, setList] = useState([]);
  function gen() {
    const make = () => {
      if (crypto.randomUUID) return crypto.randomUUID();
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
    };
    setList(Array.from({ length: count }, make));
  }
  React.useEffect(() => { gen(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-5">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-700">How many?</label>
          <input type="number" min="1" max="500" className="input mt-2" value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="uuid-count" />
        </div>
        <button onClick={gen} className="btn-primary" data-testid="uuid-gen-btn"><RefreshCw size={16} /> Generate</button>
      </div>
      {list.length > 0 && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="uuid-output">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">{list.length} UUIDs</span>
            <CopyBtn value={list.join("\n")} testid="uuid-copy" />
          </div>
          <pre className="font-mono text-xs leading-6 text-foreground max-h-96 overflow-auto">{list.join("\n")}</pre>
        </div>
      )}
    </div>
  );
}

function PasswordRunner() {
  const [length, setLength] = useState(20);
  const [opts, setOpts] = useState({ upper: true, lower: true, num: true, sym: true });
  const [value, setValue] = useState("");
  function gen() {
    const sets = [
      opts.upper && "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      opts.lower && "abcdefghijklmnopqrstuvwxyz",
      opts.num && "0123456789",
      opts.sym && "!@#$%^&*()-_=+[]{};:,.<>/?",
    ].filter(Boolean);
    if (!sets.length) return setValue("Select at least one set");
    const pool = sets.join("");
    const arr = new Uint32Array(length);
    crypto.getRandomValues(arr);
    let out = "";
    for (let i = 0; i < length; i++) out += pool[arr[i] % pool.length];
    setValue(out);
  }
  React.useEffect(() => { gen(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-medium text-gray-700">Length: {length}</label>
        <input type="range" min="6" max="64" value={length} onChange={(e) => setLength(Number(e.target.value))} className="w-full mt-2" data-testid="pwd-length" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[["upper","Uppercase"],["lower","Lowercase"],["num","Numbers"],["sym","Symbols"]].map(([k,l]) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({...opts, [k]: e.target.checked})} data-testid={`pwd-${k}`} />
            {l}
          </label>
        ))}
      </div>
      <button onClick={gen} className="btn-primary" data-testid="pwd-gen-btn"><RefreshCw size={16} /> Generate</button>
      {value && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="pwd-output">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Password</span>
            <CopyBtn value={value} testid="pwd-copy" />
          </div>
          <pre className="font-mono text-lg break-all">{value}</pre>
        </div>
      )}
    </div>
  );
}

function WordCountRunner() {
  const [text, setText] = useState("");
  const stats = useMemo(() => {
    const t = text;
    const words = t.trim() ? t.trim().split(/\s+/).filter(Boolean).length : 0;
    const chars = t.length;
    const charsNoSpace = t.replace(/\s/g, "").length;
    const sentences = (t.match(/[.!?]+(\s|$)/g) || []).length;
    const paragraphs = t.split(/\n+/).filter((p) => p.trim()).length;
    const reading = Math.max(1, Math.round(words / 200));
    return { words, chars, charsNoSpace, sentences, paragraphs, reading };
  }, [text]);
  return (
    <div className="space-y-5">
      <textarea className="input" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Start typing or paste content…" data-testid="wc-input" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="wc-stats">
        {[
          ["Words", stats.words],
          ["Characters", stats.chars],
          ["No spaces", stats.charsNoSpace],
          ["Sentences", stats.sentences],
          ["Paragraphs", stats.paragraphs],
          ["Reading time", `${stats.reading} min`],
        ].map(([l, v]) => (
          <div key={l} className="border border-border rounded-lg p-4 bg-card">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">{l}</div>
            <div className="font-heading text-2xl font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CaseRunner() {
  const [text, setText] = useState("Hello World — make me different cases.");
  const conv = {
    "UPPER": (s) => s.toUpperCase(),
    "lower": (s) => s.toLowerCase(),
    "Title Case": (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
    "Sentence case": (s) => s.toLowerCase().replace(/(^\s*\w|[.!?]\s+\w)/g, (c) => c.toUpperCase()),
    "camelCase": (s) => s.toLowerCase().replace(/[^a-z0-9]+(.)/g, (_, c) => c.toUpperCase()).replace(/^./, (c) => c.toLowerCase()),
    "PascalCase": (s) => s.toLowerCase().replace(/(^|[^a-z0-9])(.)/g, (_, __, c) => c.toUpperCase()),
    "kebab-case": (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
    "snake_case": (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
  };
  return (
    <div className="space-y-5">
      <textarea className="input" rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="case-input" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(conv).map(([label, fn]) => {
          const out = fn(text);
          return (
            <div key={label} className="border border-border rounded-lg p-4 bg-card" data-testid={`case-${label.replace(/\s/g,'-').toLowerCase()}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-gray-400">{label}</span>
                <CopyBtn value={out} testid={`case-copy-${label.replace(/\s/g,'-').toLowerCase()}`} />
              </div>
              <div className="font-mono text-sm break-all">{out}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LoremRunner() {
  const [n, setN] = useState(3);
  const [out, setOut] = useState("");
  function gen() {
    const sentences = [
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
      "Duis aute irure dolor in reprehenderit in voluptate velit esse.",
      "Excepteur sint occaecat cupidatat non proident, sunt in culpa.",
      "Curabitur pretium tincidunt lacus, nulla gravida orci a odio.",
      "Nullam varius, turpis et commodo pharetra, est eros bibendum elit.",
    ];
    const paras = Array.from({ length: n }, (_, i) => {
      const len = 4 + ((i * 3) % 4);
      return Array.from({ length: len }, (_, j) => sentences[(i * 3 + j) % sentences.length]).join(" ");
    });
    setOut(paras.join("\n\n"));
  }
  React.useEffect(() => { gen(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-5">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="text-sm font-medium text-gray-700">Paragraphs</label>
          <input type="number" min="1" max="20" className="input mt-2" value={n} onChange={(e) => setN(Number(e.target.value))} data-testid="lorem-count" />
        </div>
        <button onClick={gen} className="btn-primary" data-testid="lorem-gen-btn"><RefreshCw size={16} /> Generate</button>
      </div>
      {out && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="lorem-output">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Output</span>
            <CopyBtn value={out} testid="lorem-copy" />
          </div>
          <pre className="whitespace-pre-wrap text-[15px] leading-relaxed">{out}</pre>
        </div>
      )}
    </div>
  );
}

/* =========== ROUTER =========== */
function Runner({ tool }) {
  switch (tool.kind) {
    case "ai": return <AIRunner tool={tool} />;
    case "qr": return <QRRunner />;
    case "json": return <JsonRunner />;
    case "base64": return <Base64Runner />;
    case "urlenc": return <UrlRunner />;
    case "uuid": return <UuidRunner />;
    case "password": return <PasswordRunner />;
    case "wordcount": return <WordCountRunner />;
    case "case": return <CaseRunner />;
    case "lorem": return <LoremRunner />;
    default: return <FileUploadRunner tool={tool} />;
  }
}

export default function ToolPage() {
  const { slug } = useParams();
  const tool = getTool(slug);

  if (!tool) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-gray-500">Tool not found.</p>
        <Link to="/" className="text-primary hover:underline mt-3 inline-block">Browse all tools</Link>
      </div>
    );
  }

  const cat = CATEGORIES.find((c) => c.id === tool.cat);
  const Icon = tool.icon;
  const related = TOOLS.filter((t) => t.cat === tool.cat && t.slug !== tool.slug).slice(0, 4);

  const seo = (
    <Seo
      title={`${tool.name} — Free Online, No Signup | ToolHaus`}
      description={`${tool.desc} 100% free, no signup, no watermarks. Works in your browser.`}
      path={`/tool/${tool.slug}`}
      jsonLd={toolJsonLd(tool)}
    />
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {seo}
      <Link to="/" className="text-sm text-gray-500 hover:text-primary inline-flex items-center gap-1 mb-8" data-testid="back-home">
        <ArrowLeft size={14} /> All tools
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${cat?.accent}14`, color: cat?.accent }}>
              <Icon size={20} strokeWidth={1.75} />
            </div>
            <Link to={`/category/${cat?.id}`} className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400 hover:text-foreground">
              {cat?.label}
            </Link>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="tool-title">
            {tool.name}
          </h1>
          <p className="text-gray-500 mt-2 max-w-2xl">{tool.desc}</p>

          <div className="mt-8">
            <Runner tool={tool} />
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <AdSlot variant="sidebar" testid="ad-tool-sidebar" />
          <div className="border border-border rounded-xl p-5 bg-card">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400 mb-3">Why ToolHaus</div>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-2"><ShieldCheck size={16} className="text-emerald-500 shrink-0 mt-0.5" /> No signup, no watermark, no captcha.</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-primary shrink-0 mt-0.5" /> Designed for speed — most tasks under 2 seconds.</li>
              <li className="flex gap-2"><RefreshCw size={16} className="text-gray-500 shrink-0 mt-0.5" /> Files never stored — processed and returned.</li>
            </ul>
          </div>
          {related.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400 mb-3">Related tools</div>
              <div className="space-y-3">
                {related.map((t) => <ToolCard key={t.slug} tool={t} />)}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
